import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Network, GitBranch, TrendingUp, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import GenealogyTree from "@/components/GenealogyTree";

function StatTile({ icon: Icon, label, value, tint = "bg-primary/10 text-primary" }) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</div>
          <div className="text-2xl font-semibold mt-1">{value ?? 0}</div>
        </div>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${tint}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function IBOGenealogy() {
  const [roots, setRoots] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/ibo/hierarchy")
      .then(({ data }) => setRoots(data.roots || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalRoots = roots.length;
  const totalMembers = roots.reduce((s, r) => s + (r.team_size || 1), 0);
  const totalBills = roots.reduce((s, r) => s + (r.team_bill_count || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">IBO Genealogy</h1>
          <p className="text-sm text-muted-foreground">
            Full company hierarchy. Each root is a top-level IBO or admin who is an upline. Expand to drill in.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-genealogy">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatTile icon={GitBranch} label="Top-level roots" value={totalRoots} tint="bg-blue-100 text-blue-700" />
        <StatTile icon={Network} label="Total members" value={totalMembers} tint="bg-emerald-100 text-emerald-700" />
        <StatTile icon={TrendingUp} label="Total B2B bills (all teams)" value={totalBills} tint="bg-purple-100 text-purple-700" />
      </div>

      <GenealogyTree
        roots={roots}
        emptyLabel={loading ? "Loading hierarchy…" : "No IBOs in the hierarchy yet."}
      />
    </div>
  );
}
